import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { Thumbnail, ListItemElement, SizeDetectorService } from '@symplicity/syng';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActionButton, ActionButtonClick } from '../action-buttons/action-buttons.component';

export interface EventViewHeader {
    banner: Thumbnail | null;
    title: string;
    subtitle: string;
    tags: ListItemElement[];
}

@Component({
    selector: 'cf-event-view-header',
    templateUrl: './event-view-header.component.html'
})
export class EventViewHeaderComponent implements AfterViewInit, OnDestroy {
    @Input()
    public headerData: EventViewHeader;

    @Input()
    public buttonsData: ActionButton[] = [];

    @Output()
    public readonly buttonClick = new EventEmitter<ActionButtonClick>();

    private stickyHeader = false;
    private stickyBreakpoint = 0;
    private isMobile = false;
    private elements = {
        container: null,
        header: null,
        content: null,
        title: null
    };
    private destroyed$ = new Subject<void>();

    constructor(
        private elementRef: ElementRef,
        private window: Window,
        private sizeDetectorService: SizeDetectorService,
        @Inject(DOCUMENT) private document: Document
    ) {}

    public ngAfterViewInit(): void {
        this.stickyBreakpoint = this.getStickyBreakpoint();

        this.elements = {
            container: this.elementRef.nativeElement.querySelector('.header-content-container'),
            header: this.elementRef.nativeElement.querySelector('.careerfair-header-wrapper'),
            content: this.elementRef.nativeElement.querySelector('.careerfair-header-wrapper .careerfair-header'),
            title: this.elementRef.nativeElement.querySelector('.careerfair-header h1')
        };

        let execOnScroll = this.window.scrollY === 0;

        fromEvent(this.window, 'scroll').pipe(
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            if (execOnScroll) {
                this.onScroll();
            } else {
                execOnScroll = this.window.scrollY === 0;
            }
        });

        this.sizeDetectorService.onResize$.pipe(
            takeUntil(this.destroyed$)
        ).subscribe(screen => this.onResize(screen));
    }

    public ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    public onButtonClick(data: ActionButtonClick): void {
        this.buttonClick.emit(data);
    }

    private getStickyBreakpoint(): number {
        const bannerHeight = this.headerData.banner
            ? this.elementRef.nativeElement.querySelector('.page-banner-image').offsetHeight
            : 0;
        const bodyTop = parseInt(
            this.window.getComputedStyle(this.document.body).getPropertyValue('margin-top')
        );
        const componentY = this.elementRef.nativeElement.getBoundingClientRect().y;
        const scrollY = this.window.scrollY

        return (componentY + scrollY + bannerHeight - bodyTop);
    }

    private onScroll(): void {
        if (!this.isMobile && !this.stickyHeader && this.window.scrollY >= this.stickyBreakpoint) {
            this.setStickyHeader(true);
        } else if (this.stickyHeader && this.window.scrollY < this.stickyBreakpoint) {
            this.setStickyHeader(false);
        }
    }

    private onResize(screen: any): void {
        this.isMobile = screen.size === 'MOBILE' || screen.size === 'SM';
        this.stickyBreakpoint = this.getStickyBreakpoint();

        if (this.isMobile) {
            this.setStickyHeader(false);
        }

        if (this.stickyHeader) {
            this.setContentWidth(true);
        }
    }

    private setStickyHeader(isSticky: boolean): void {
        this.stickyHeader = isSticky;
        this.setContentWidth(isSticky);

        if (isSticky) {
            this.elementRef.nativeElement.style.height = `${this.elements.container.offsetHeight}px`;

            this.elements.header.classList.add('sticky-header');
            this.elements.title.classList.add('text-truncate', 'h3-text');
        } else {
            this.elementRef.nativeElement.style.height = null;

            this.elements.header.classList.remove('sticky-header');
            this.elements.title.classList.remove('text-truncate', 'h3-text');
        }
    }

    private setContentWidth(isSticky: boolean): void {
        if (isSticky) {
            this.elements.content.style.width = `${this.elementRef.nativeElement.offsetWidth}px`;
        } else {
            this.elements.content.style.width = null;
        }
    }
}
